import { Schema, Effect } from "effect";
import { describeIntRange } from "../openapi-query.js";
import { OpenApiClass } from "../openapi-class.js";
import { UUID } from "../uuid.js";

const Count = Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)));
const Share = Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 })));
const Keyword = Schema.NonEmptyString.pipe(
  Schema.check(Schema.isPattern(/\S/u)),
  Schema.check(Schema.isMaxLength(256))
);

const filters = {
  // Existing clients retain immutable observation drill-down until they opt into live evidence.
  view: Schema.Literals(["open", "observation"]).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "observation" as const)),
    Schema.withConstructorDefault(Effect.sync(() => "observation" as const))
  ),
  scope: Schema.Literals(["company", "group"]).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "company" as const)),
    Schema.withConstructorDefault(Effect.sync(() => "company" as const))
  ),
  days: Schema.NumberFromString.pipe(
    Schema.refine((n) => n === 0 || n === 30 || n === 90),
    Schema.annotateEncoded({ description: "one of 0, 30 or 90" })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  industry: Schema.String.pipe(Schema.check(Schema.isMaxLength(100))).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "")),
    Schema.withConstructorDefault(Effect.sync(() => ""))
  ),
};
const pagination = {
  offset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isBetween({ minimum: 0, maximum: 100_000 })),
    describeIntRange({ minimum: 0, maximum: 100_000 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  limit: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isBetween({ minimum: 1, maximum: 100 })),
    describeIntRange({ minimum: 1, maximum: 100 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 30)),
    Schema.withConstructorDefault(Effect.sync(() => 30))
  ),
};

/** Aggregate observations; shares measure analyzed ads, not a share of the entire market. */
export class HiringTrendsQuery extends Schema.Class<HiringTrendsQuery>("HiringTrendsQuery")({
  ...filters,
  ...pagination,
  search: Schema.String.pipe(Schema.check(Schema.isMaxLength(100))).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "")),
    Schema.withConstructorDefault(Effect.sync(() => ""))
  ),
  sort: Schema.Literals(["volume", "rising"]).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "volume" as const)),
    Schema.withConstructorDefault(Effect.sync(() => "volume" as const))
  ),
}) {}

export class HiringTrendCompaniesQuery extends Schema.Class<HiringTrendCompaniesQuery>(
  "HiringTrendCompaniesQuery"
)({
  ...filters,
  ...pagination,
  keyword: Keyword,
}) {}

/** Live drill-down uses orgnr and filters; legacy snapshot/revision requests remain pinned. */
export class HiringTrendAdsQuery extends Schema.Class<HiringTrendAdsQuery>("HiringTrendAdsQuery")({
  ...filters,
  ...pagination,
  orgnr: Schema.optional(Schema.NonEmptyString.pipe(Schema.check(Schema.isMaxLength(32)))),
  snapshotId: Schema.optional(UUID),
  revision: Schema.optional(
    Schema.NumberFromString.pipe(
      Schema.check(Schema.isInt()),
      Schema.check(Schema.isGreaterThan(0)),
      describeIntRange({ minimum: 1 })
    )
  ),
  keyword: Keyword,
}) {}

const Page = Schema.Struct({
  offset: Count,
  limit: Count,
  total: Count,
  nextOffset: Schema.NullOr(Count),
});

/** Coverage is explicit; only fully analyzed, comparable company pairs contribute to change. */
export class HiringTrendsCoverage extends OpenApiClass<HiringTrendsCoverage>(
  "HiringTrendsCoverage"
)({
  candidateCompanies: Count,
  qualifiedCompanies: Count,
  observedCompanies: Count,
  analyzedCompanies: Count,
  comparisonCompanies: Count,
  totalAds: Count,
  analyzedAds: Count,
  comparisonCurrentAds: Count,
  comparisonPreviousAds: Count,
  oldestObservationAt: Schema.NullOr(Schema.String),
  newestObservationAt: Schema.NullOr(Schema.String),
}) {}

export class HiringTrendKeyword extends OpenApiClass<HiringTrendKeyword>("HiringTrendKeyword")({
  keyword: Schema.String,
  adCount: Count,
  companyCount: Count,
  share: Share,
  comparisonAdCount: Schema.NullOr(Count),
  previousAdCount: Schema.NullOr(Count),
  changePp: Schema.NullOr(Schema.Finite),
}) {}

export class HiringTrendsResponse extends OpenApiClass<HiringTrendsResponse>(
  "HiringTrendsResponse"
)({
  asOf: Schema.String,
  cohortRevision: Schema.String,
  scope: Schema.Literals(["company", "group"]),
  days: Schema.Number,
  coverage: HiringTrendsCoverage,
  items: Schema.Array(HiringTrendKeyword),
  page: Page,
}) {}

export class HiringTrendCompany extends OpenApiClass<HiringTrendCompany>("HiringTrendCompany")({
  orgnr: Schema.String,
  name: Schema.NullOr(Schema.String),
  adCount: Count,
  observedAt: Schema.String,
  snapshotId: Schema.NullOr(UUID),
  revision: Schema.NullOr(Count),
}) {}

export class HiringTrendCompaniesResponse extends OpenApiClass<HiringTrendCompaniesResponse>(
  "HiringTrendCompaniesResponse"
)({
  asOf: Schema.String,
  cohortRevision: Schema.String,
  items: Schema.Array(HiringTrendCompany),
  page: Page,
}) {}

export class HiringTrendAd extends OpenApiClass<HiringTrendAd>("HiringTrendAd")({
  id: Schema.String,
  title: Schema.NullOr(Schema.String),
  sourceUrl: Schema.String,
  lastSeenAt: Schema.String,
}) {}

export class HiringTrendAdsResponse extends OpenApiClass<HiringTrendAdsResponse>(
  "HiringTrendAdsResponse"
)({
  snapshotId: Schema.NullOr(UUID),
  revision: Schema.NullOr(Count),
  cohortRevision: Schema.NullOr(Schema.String),
  orgnr: Schema.String,
  scope: Schema.Literals(["company", "group"]),
  observedAt: Schema.String,
  keyword: Schema.String,
  items: Schema.Array(HiringTrendAd),
  page: Page,
}) {}
